import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Search } from 'lucide-react'
import { Link } from 'react-router-dom'

const faqCategories = [
  {
    name: 'Products',
    faqs: [
      {
        question: 'What materials are your teethers made from?',
        answer:
          'All Petite Palate teethers are made from 100% food-grade silicone. They are BPA-free, PVC-free, phthalate-free, and completely non-toxic. Our materials exceed Australian safety standards for baby products.',
      },
      {
        question: 'What age are your teethers suitable for?',
        answer:
          'Our teethers are suitable for babies from birth onwards. Each product listing includes a recommended age range based on the texture complexity and grip requirements. Our softest teethers are perfect for newborns, while more textured options suit babies 3+ months.',
      },
      {
        question: 'How do I clean the teethers?',
        answer:
          'Our teethers are dishwasher safe (top rack) or can be washed with warm soapy water. For sterilisation, they can be boiled for 5 minutes or placed in a steam steriliser. Avoid using harsh chemicals or bleach.',
      },
      {
        question: 'Can I freeze the teethers?',
        answer:
          'Yes! Our silicone teethers are freezer safe. The cool temperature can provide extra relief for sore, teething gums. We recommend chilling for 15-30 minutes rather than freezing solid for optimal comfort.',
      },
    ],
  },
  {
    name: 'Orders & Shipping',
    faqs: [
      {
        question: 'How long does shipping take?',
        answer:
          'Standard shipping within Australia takes 3-5 business days. Express shipping (1-2 business days) is available at checkout. All orders are dispatched from Melbourne within 1 business day.',
      },
      {
        question: 'Do you offer free shipping?',
        answer:
          'Yes! We offer free standard shipping on all orders over $75 within Australia. Orders under $75 have a flat rate shipping fee of $9.95.',
      },
      {
        question: 'Do you ship internationally?',
        answer:
          "Yes, we ship worldwide! Australian orders enjoy free standard shipping over $75. International shipping rates are calculated at checkout based on your destination. Delivery times vary by location.",
      },
      {
        question: 'Can I track my order?',
        answer:
          "Yes, you'll receive a tracking number via email once your order has been dispatched. You can also track your order in your account under 'Order History'.",
      },
    ],
  },
  {
    name: 'Returns & Warranty',
    faqs: [
      {
        question: 'What is your return policy?',
        answer:
          'We offer a 30-day satisfaction guarantee. If you\'re not completely happy with your purchase, you can return unused items in their original packaging for a full refund. Contact us at hello@petitepalate.com.au to initiate a return.',
      },
      {
        question: 'What if my teether arrives damaged?',
        answer:
          "We take great care in packaging, but if your item arrives damaged, please contact us within 7 days with photos of the damage. We'll send a replacement at no extra cost.",
      },
      {
        question: 'Do your products have a warranty?',
        answer:
          'Yes, all Petite Palate products come with a 12-month warranty against manufacturing defects. This covers issues like material degradation, colour fading, or structural defects under normal use.',
      },
    ],
  },
  {
    name: 'Safety & Care',
    faqs: [
      {
        question: 'Are your teethers safe for babies?',
        answer:
          'Absolutely. Safety is our top priority. All products undergo rigorous testing and comply with Australian safety standards (AS/NZS 8124). They are designed without small parts or choking hazards.',
      },
      {
        question: 'Should I supervise my baby while using the teether?',
        answer:
          'Yes, we recommend supervising your baby during use. While our teethers are designed to be safe, it\'s always best practice to watch little ones during play and teething.',
      },
      {
        question: 'How do I know when to replace a teether?',
        answer:
          'Inspect teethers regularly for signs of wear such as tears, holes, or discolouration. If you notice any damage, discontinue use immediately and replace with a new teether.',
      },
    ],
  },
]

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border-b border-pp-sand last:border-0">
      <button
        onClick={onToggle}
        className="w-full py-5 flex items-center justify-between text-left"
      >
        <span className="font-medium text-pp-ink pr-8">{faq.question}</span>
        <ChevronDown
          className={`w-5 h-5 text-pp-stone flex-shrink-0 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-pp-stone leading-relaxed">{faq.answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function FAQ() {
  const [openItems, setOpenItems] = useState({})
  const [searchQuery, setSearchQuery] = useState('')

  const toggleItem = (categoryIndex, faqIndex) => {
    const key = `${categoryIndex}-${faqIndex}`
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const filteredCategories = faqCategories
    .map((category) => ({
      ...category,
      faqs: category.faqs.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((category) => category.faqs.length > 0)

  return (
    <div className="min-h-screen bg-pp-ivory">
      {/* Hero */}
      <section className="bg-pp-linen py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="font-display text-display-lg text-pp-ink mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-pp-stone text-lg leading-relaxed mb-8">
              Find answers to common questions about our products, shipping, and
              more.
            </p>

            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pp-stone" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-pp-sand rounded-lg bg-white text-pp-ink placeholder-pp-stone/60 focus:outline-none focus:ring-2 focus:ring-pp-terracotta/20 focus:border-pp-terracotta transition-colors"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto">
            {filteredCategories.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-pp-stone">
                  No questions found matching "{searchQuery}".
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 text-pp-terracotta hover:underline"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="space-y-12">
                {filteredCategories.map((category, categoryIndex) => (
                  <motion.div
                    key={category.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                  >
                    <h2 className="font-display text-xl text-pp-ink mb-4">
                      {category.name}
                    </h2>
                    <div className="bg-white rounded-2xl border border-pp-sand/50 px-6">
                      {category.faqs.map((faq, faqIndex) => (
                        <FAQItem
                          key={faqIndex}
                          faq={faq}
                          isOpen={openItems[`${categoryIndex}-${faqIndex}`]}
                          onToggle={() => toggleItem(categoryIndex, faqIndex)}
                        />
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 lg:py-24 bg-pp-linen">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-display-sm text-pp-ink mb-4">
              Still have questions?
            </h2>
            <p className="text-pp-stone mb-8">
              We're here to help. Reach out and we'll get back to you within 24
              hours.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-pp-terracotta text-white rounded-lg hover:bg-pp-terracotta/90 transition-colors"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
